import React, { useEffect, useMemo } from 'react'
import { Box, Divider, useMediaQuery } from '@mui/material'
import type { AppBarProps } from '@mui/material'
import { StyledAppBar, StyledToolbar } from './styles'
import classrankedLogo from '../../assets/classRankedLogo.svg'
import { useTheme } from '@mui/material/styles'
import TabMenu from './TabMenu/index'
import { MobileTabMenu } from './MobileTabMenu'
import { useQuery } from '@tanstack/react-query'
import { UserManager } from '../../APIManager'
import { CURRENT_ROLE_ID_KEY, ROLES } from '../../consts/roles'
import { getPagePermissions } from '../../store/slices/roleSlice/selectors'
import ROUTES from '../../consts/paths'
import { useUserProfile } from '../../hooks/useUserProfile'
import { resetFavicon, setFavicon } from '../../utils/favicon'
import { useUserRole } from '../../hooks/useUserRole'

interface CustomAppBarProps extends AppBarProps {
  onClick?: () => void
  isTabMenu?: boolean
  isPublic?: boolean
}
const CustomAppBar = function ({
  isTabMenu = true,
  onClick = () => { },
  isPublic = false
}: CustomAppBarProps): JSX.Element {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const pagePermissions = getPagePermissions()
  const hasXUserRole = !!localStorage.getItem(CURRENT_ROLE_ID_KEY)
  const { data: userProfile } = useUserProfile()
  const { activeRole, isGlobalOrGenericRole } = useUserRole()

  const selectedRoleTypeIsSection = activeRole?.roleType === 'SECTION'
  const isPrimaryInstructor = activeRole?.roleName === ROLES.PRIMARY_INSTRUCTOR
  const isInstructorDashboard = window.location.pathname.startsWith(ROUTES.INSTRUCTOR_SURVEYS)
  const shouldSkipApiCall = (isPrimaryInstructor && isInstructorDashboard) || selectedRoleTypeIsSection

  useQuery({
    queryKey: ['topBarDivisions'],
    queryFn: async () => {
      const response = await UserManager.getTopBarDivisions()
      return response?.data
    },
    retry: false,
    enabled: !isPublic && hasXUserRole && (pagePermissions?.includes('Report - Page') ?? false) && !shouldSkipApiCall && isGlobalOrGenericRole,
    refetchOnMount: false
  })

  const tenant = localStorage.getItem('tenantKey') ?? undefined
  const customBrandingEnabled = !!userProfile?.isCustomBrandingEnabled
  const extensions = ['png', 'jpg', 'jpeg', 'svg'] // Supported image formats for custom logos and icons

  // Primary Logo Update Logic
  const brand = useMemo(() => {
    const result = {
      src: classrankedLogo,
      alt: 'ClassRanked logo',
      ariaLabel: 'ClassRanked logo'
    }

    if (customBrandingEnabled && tenant) {
      let extCounter = 0
      for (const ext of extensions) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          result.src = require(`../../assets/custom-branding/logos/${tenant}.${ext}`)
          result.alt = `${tenant} logo`
          result.ariaLabel = `${tenant} logo`
          break
        } catch {
          if (extCounter === extensions.length - 1) {
            console.warn(`Custom logo for tenant "${tenant}" not found.`)
          }
          extCounter++
        }
      }
    }

    return result
  }, [tenant, customBrandingEnabled])

  // Favicon Update Logic
  useEffect(() => {
    if (!tenant || !customBrandingEnabled) {
      resetFavicon()
      return
    }

    let extCounter = 0
    for (const ext of extensions) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const customIcon = require(`../../assets/custom-branding/icons/${tenant}.${ext}`)
        setFavicon(customIcon)
        return
      } catch {
        if (extCounter === extensions.length - 1) {
          console.warn(`Custom icon for tenant "${tenant}" not found. Falling back to default icons.`)
        }
        extCounter++
        continue
      }
    }
    resetFavicon()
  }, [tenant, customBrandingEnabled])

  return (
    <StyledAppBar
      /* Anchor for useAppBarHeight (PageHeader sticky top offset) */
      data-custom-app-bar="true"
      elevation={0} >
      <StyledToolbar isMobile={isMobile}>
        <Box sx={{ cursor: 'pointer' }} display='flex' alignItems='center' justifyContent={isMobile ? 'space-between' : 'unset'} gap={theme.spacing(5.5)} width='100%'>
          <Box display='flex' alignItems='center' gap={theme.spacing(4)}>
            <img
              aria-label={brand.ariaLabel}
              tabIndex={0}
              alt={brand.alt}
              width={'120px'}
              height={'36px'}
              onClick={() => { window.location.href = '/' }}
              src={brand.src}
            />
          </Box>
          {!isPublic && isTabMenu && !isMobile && <TabMenu />}
          {!isPublic && isTabMenu && isMobile && <MobileTabMenu />}
        </Box>
      </StyledToolbar>
      <Divider />
    </StyledAppBar>
  )
}

export default CustomAppBar
